import classNames from "classnames"
import { useState } from "react"
import { Task } from "../../../../types"
import { Button } from "../../../components/button/Button"
import { DeleteIcon, IconButton } from "../../../components/button/IconButton"
import { deleteTask, finishTask } from "../../../fetch/tasks"
import { formatTime } from "../../../lib/formatTime"
import styles from "./sidepanel.module.css"
import { isBitus } from "../../../../config/urls"
import { useWatcher } from "../../../lib/state/useWatcher"
import { tenantState } from "../../../store/tenant"
import { ConfirmDelete } from "../../../components/confirm/ConfirmDelete"
import { Form } from "@/components/ui/form"
import { useForm, SubmitHandler } from "react-hook-form"
import styled from "styled-components"
import { InputField } from "@/components/ui/form/InputField"
import { EditTask, EditTaskPosition } from "./EditTaskPosition"
import { LoaderCircle } from "lucide-react"
import { toast } from "react-toastify"
import { formatDate } from "@/lib/formatDate"

const Row = styled.div`
  display: grid;
  grid-template-columns: repeat(2, calc(50% - 8px));
  gap: 16px;
`

type Props = {
  task?: Task
  onClose: (close?: boolean, reload?: boolean) => void
}

export const ConfirmTask = ({ task, onClose }: Props) => {
  const {
    settings: { features },
  } = useWatcher(tenantState)
  const units = task?.unitsUsed ?? 0
  const salary = (task?.salaryUsed ?? 0) / 100
  const [unitsPredicted, setUnitsPredicted] = useState(task?.unitsPredicted ?? 0)
  const salaryPredicted = ((task?.salaryPredicted ?? 0) / 100).toFixed(2)
  const projectId = task?.projectId
  const subprojectId = task?.subProjectId
  const operationId = task?.operationId

  const [loading, setLoading] = useState(false)

  const form = useForm<EditTask>({
    defaultValues: {
      timeUsed: {
        hours: Math.floor((task?.timeUsed ?? 0) / 60),
        minutes: (task?.timeUsed ?? 0) % 60,
      },
      unitsUsed: `${units}`,
      salaryUsed: salary,
      projectId: [projectId!],
      subprojectId: [subprojectId!],
      operationId: [operationId!],
      date: formatDate(
        new Date(task?.date?.replace(/(\d+)\.(\d+)\.(\d+)/, "$3-$2-$1") ?? ""),
      ),
    },
    shouldUnregister: false,
  })

  const handleSave: SubmitHandler<EditTask> = async (values, e) => {
    const submitEvent = e?.nativeEvent as SubmitEvent
    const button = submitEvent.submitter as HTMLButtonElement | null
    const approve = button?.name === "approve"
    if (!task) {
      return
    }
    setLoading(true)
    const { timeUsed, unitsUsed, salaryUsed, date } = values
    const body: any = {
      // status: 1,
      projectId: +values.projectId,
      subprojectId: values.subprojectId && +values.subprojectId,
      operationId: values.operationId && +values.operationId,
      timeUsed: timeUsed.hours * 60 + timeUsed.minutes,
      date: formatDate(new Date(date)),
    }

    if (approve) {
      body.status = 1
    }

    let hasError = false

    if (!date) {
      form.setError("date", { type: "required" })
      hasError = true
    }

    if (timeUsed.hours < 0 || timeUsed.minutes < 0) {
      form.setError("timeUsed", { type: "required" })
      hasError = true
    }

    if (features["salary"]) {
      if (salaryUsed < 0) {
        form.setError("salaryUsed", { type: "required" })
        hasError = true
      } else {
        body.salayUsed = salaryUsed * 100
      }
    }

    if (features["units"]) {
      if (unitsUsed === "") {
        form.setError("unitsUsed", { type: "required" })
        hasError = true
      } else {
        body.unitsUsed = +unitsUsed || 0
      }
    }

    if (hasError) {
      setLoading(false)
      return
    }

    try {
      await finishTask(task?.id, body)
      toast(
        approve
          ? "Uzdevums veiksmīgi apstiprināts!"
          : "Uzdevums veiksmīgi labots!",
        { type: "success" },
      )
      setLoading(false)
      // close only on approval
      onClose(approve, true)
    } catch (err) {
      console.log(err)
      setLoading(false)
      toast("Darbība nav izdevusies, lūdzu mēģiniet vēlreiz", { type: "error" })
    }
  }
  const handleDeleteTask = async (comment?: string) => {
    if (task?.id) {
      await deleteTask(task.id, comment)
      onClose(true, true)
    }
  }

  if (form.formState.isLoading) {
    return null
  }
  return !task ? null : (
    <div className={classNames(styles.sidepanel, task ? styles.open : "")}>
      <div className={styles.wrap}>
        <div className={styles.close}>
          <Button variant="inline" onClick={() => onClose(true)}>
            Aizvērt
          </Button>
        </div>
        <div className={styles.subTitle}>
          <b>{task.workerName}</b>
        </div>
        <div>{task?.createdAt && formatTime(task.createdAt)}</div>
        <div className="my-6 text-lg font-medium">
          {task?.projectName} / {task?.subProjectName} / {task?.operationName}
        </div>

        {task?.comment ? (
          <>
            <label style={{ fontSize: 16, marginTop: 16, display: "block" }}>
              Piezīmes
            </label>
            <div className={styles.blockQuote}>{task.comment}</div>
          </>
        ) : null}
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSave)}>
            <EditTaskPosition
              control={form.control}
              form={form}
              onOperationChange={(op) => setUnitsPredicted(op?.unitsPredicted ?? 0)}
            />

            <Row>
              <InputField
                control={form.control}
                name="date"
                placeholder="Datums"
                label="Datums"
                error={form.formState.errors.date}
                inputProps={{ type: "date", min: "2024-07-07" }}
              />
              <InputField
                control={form.control}
                name="timeUsed.hours"
                placeholder="Stundas"
                label="Stundas"
                error={form.formState.errors.timeUsed?.hours}
                inputProps={{ type: "number" }}
              />
              <InputField
                control={form.control}
                name="timeUsed.minutes"
                placeholder="Minūtes"
                label="Minūtes"
                error={form.formState.errors.timeUsed?.minutes}
                inputProps={{ type: "number" }}
              />
              {features["units"] && (
                <InputField
                  control={form.control}
                  label={`Vienības (paredzēts ${unitsPredicted})`}
                  name="unitsUsed"
                  placeholder="Vienības"
                  error={form.formState.errors.unitsUsed}
                  inputProps={{ type: "number" }}
                />
              )}

              {features["salary"] && (
                <InputField
                  control={form.control}
                  label={`Summa (aprēķins ${salaryPredicted})`}
                  name="salaryUsed"
                  placeholder="Summa"
                  error={form.formState.errors.salaryUsed}
                  inputProps={{ type: "number" }}
                />
              )}
            </Row>

            <div className="mt-12">
              <div className={styles.buttons}>
                <ConfirmDelete
                  Trigger={(props) => (
                    <IconButton type="button" {...props}>{DeleteIcon}</IconButton>
                  )}
                  onConfirm={handleDeleteTask}
                  commentRequired
                />

                <div style={{ display: "flex", gap: 8 }}>
                  <Button
                    type="button"
                    variant="secondary"
                    onClick={() => onClose(true, false)}
                  >
                    Atcelt
                  </Button>
                  <Button type="submit" name="save" disabled={loading}>
                    {loading ? (
                      <LoaderCircle className="animate-spin" />
                    ) : (
                      "Labot"
                    )}
                  </Button>
                  <Button type="submit" name="approve" disabled={loading}>
                    {loading ? (
                      <LoaderCircle className="animate-spin" />
                    ) : (
                      "Labot un Apstiprināt"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </form>
        </Form>
      </div>
    </div>
  )
}
